// NPM Imports
import moment from "moment";
// import amplitude from "amplitude-js";
import {
  EditorState,
  ContentState,
  convertToRaw,
  convertFromRaw,
} from "draft-js";

// Internal Imports
import {
  AUTH,
  ERROR_MSG,
  FEATURES,
  SETTINGS,
  EVENTS,
  PATHS,
  ASSET,
} from "../constants";
import store from "../store";
import { DOCUMENT_INTERNAL_STAGE } from "../constants/pipelineV2";
import { ENTERPRISE_PLANS } from "../constants/billing";

// Destructuring
const { API_ERRORS } = ERROR_MSG;
const { FREE_TRAIL, SETTINGS_VIEWS } = SETTINGS;
const { LOCAL, AUTH_COOKIE_DOMAIN } = AUTH;
const { ASSET_PAGE_URL } = ASSET;
const { FEATURES_NAME } = FEATURES;

// Declarations
let gridApi = null;
const intRegex = /^[0-9\b]+$/;

/*
==========================================
         Value Mutation Functions
==========================================
*/

export const numberForamting = (num, prefix = "", suffix = "") => {
  if (!(num === null || num === undefined || num === "")) {
    let number;
    if (intRegex.test(num)) {
      const test = num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      number = `${test}.00`;
    } else {
      const n = num.toString();
      const parts = (+n).toFixed(2).split(".");
      number =
        parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",") +
        (+parts[1] ? `.${parts[1]}` : "");
    }
    return `${prefix}${number}${suffix}`;
  }
  return `${prefix}0${suffix}`;
};

export const formatDate = (date, momentFormat) =>
  moment(date).format(momentFormat);

export function addValueAndLabelPropsForSelectOptions(
  array = [],
  propToBeCopied,
) {
  // console.debug(array);
  // React Select Library needs a value and a label property on each option
  // This function will create these 2 props on each option by duplicating the value of prop passed as 'propToBeCopied' - eg - value: item.name, label: item.name
  return array.map(item => ({
    ...item,
    value: item[propToBeCopied],
    label: item[propToBeCopied],
  }));
}

export const camelCaseToSpace = name => name.replace(/([A-Z])/g, " $1");

export const roundOffNumber = num => +`${Math.round(`${num}e+2`)}e-2`;

export const spaceToUnderscore = name => name.replace(/ /g, "_");

export const addPriceComma = num =>
  num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

export const extractWidthInt = a => {
  // method takes in '10px' and returns 10
  const b = a.slice(0, -2);
  return parseInt(b, 10);
};

/*
==========================================
     Storage Related Functions
==========================================
*/

export const readCookie = name => {
  if (typeof window === "undefined") return null;
  const cookieName = `${name}=`;
  const cookies = document.cookie.split(";");
  // eslint-disable-next-line no-restricted-syntax
  for (const key of cookies.keys()) {
    let cookie = cookies[key];
    while (cookie.charAt(0) === " ")
      cookie = cookie.substring(1, cookie.length);
    if (cookie.indexOf(cookieName) === 0)
      return cookie.substring(cookieName.length, cookie.length);
  }
  return null;
};

export const setCookie = (name, value, priority) => {
  const envName = process.env.REACT_APP_ENVIRONMENT;
  const d = new Date();
  const expiryDays = 30;
  d.setTime(d.getTime() + expiryDays * 24 * 60 * 60 * 1000);
  let cookieOptions = `secure; SameSite=Lax; expires=${d.toUTCString()};`;
  cookieOptions = priority
    ? `${cookieOptions} Priority=${priority};`
    : cookieOptions;

  if (envName === LOCAL) {
    document.cookie = `${name} = ${value}; path=/; domain=localhost; ${cookieOptions}`;
  } else {
    const domain = AUTH_COOKIE_DOMAIN[envName];
    if (domain) {
      document.cookie = `${name} = ${value}; path=/; domain=${domain}; ${cookieOptions}`;
    }
  }
};

export const removeCookie = (name) => {
  const envName = process.env.REACT_APP_ENVIRONMENT;
  // eslint-disable-next-line no-console
  console.debug(`Removing cookie param ${name} and env name ${envName}`);
  if (envName === LOCAL) {
    document.cookie = `${name} =; path=/; domain=localhost; expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
  } else {
    const domain = AUTH_COOKIE_DOMAIN[envName];
    console.debug(`Removing cookie param ${name} and domain name ${domain}`);
    if (domain) {
      document.cookie = `${name} =; path=/; domain=${domain}; expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
    }
  }
};

// splits the user object & stores in three different cookies
export const setUserCookies = user => {
  const accessToken = user.access_token;
  const refreshToken = user.refresh_token;
  const userData = { ...user };
  delete userData.access_token;
  delete userData.refresh_token;
  setCookie("accessToken", accessToken, "HIGH");
  setCookie("refreshToken", refreshToken, "HIGH");
  setCookie("user", JSON.stringify(userData), "HIGH");
};

export const removeUserCookies = () => {
  removeCookie("user");
  removeCookie("accessToken");
  removeCookie("refreshToken");
};

export const setSessionStorage = (name, value) => {
  sessionStorage.setItem(name, value);
};

export const getSessionStorage = name => {
  sessionStorage.getItem(name);
};

export const writeDataToLocalStorage = (key, value) => {
  if (typeof window !== "undefined") {
    localStorage.removeItem(key);
    localStorage.setItem(key, JSON.stringify(value));
  }
};

export const readDataFromLocalStorage = key => {
  if (typeof window !== "undefined") {
    return localStorage.getItem(key)
      ? JSON.parse(localStorage.getItem(key))
      : undefined;
  }
  return null;
};

export const removeDataFromLocalStorage = key => {
  if (typeof window !== "undefined") {
    localStorage.removeItem(key);
  }
};

export const checkLocalStorageForUserObject = () => {
  if (localStorage.getItem("revvsalesUser") === null) {
    return false;
  }
  store.dispatch({ type: "CHANGE_AUTH", payload: true });
  return true;
};

/*
==========================================
     API Error Msg Function
==========================================
*/

export const handleApiErrMsg = err => {
  if (err && err.response && err.response.data) {
    return (
      API_ERRORS[err.response.data.code] ||
      err.response.data.message ||
      API_ERRORS.DEFAULT_MSG
    );
  }
  return API_ERRORS.DEFAULT_MSG;
};

export const handleErrorMessage = error => {
  let message = API_ERRORS.DEFAULT_MSG;
  if (!error) {
    return message;
  }

  // if it is a instance of response error
  const res = error.response;
  if (res === undefined) {
    message = "There is some issue with network. Please re-try after sometime.";
  } else if (typeof res === "object") {
    const { status } = res;
    if (parseInt(status, 10) === 404) {
      message = "Requested document is not available.";
    } else if (parseInt(status, 10) === 400) {
      message =
        "Unable to fetch the requested document. Please re-try after sometime.";
    } else if (parseInt(status, 10) === 500) {
      message = "Facing backend issue. Please re-try after sometime.";
    }
  }
  return message;
};

/*
==========================================
              RBAC Functions
==========================================
*/

export const arrayHasObjectWithKeyAndVal = (array, objKey, objVal) => {
  let has = false;
  if (array) {
    for (let index = 0; index < array.length; index += 1) {
      const element = array[index];
      if (
        Object.prototype.hasOwnProperty.call(element, objKey) &&
        element[objKey] === objVal
      ) {
        has = true;
        break;
      }
    }
  }

  return has;
};

export const applyRbac = (views, permissions) => {
  const newViews = views.filter(view => {
    if (Object.prototype.hasOwnProperty.call(view, "readPermission")) {
      if (
        arrayHasObjectWithKeyAndVal(permissions, "name", view.readPermission)
      ) {
        return true;
      }
      return false;
    }
    return true;
  });
  return newViews;
};

/*
==========================================
   Products used & unused check Function
==========================================
*/
// this method looks for a particular object array1 if fails looks into array2
export const twoArrayLookUp = (array1, array2, matchID) => {
  let obj = array1.find(item => item.id === matchID);
  if (!obj) {
    obj = array2.find(item => item.id === matchID);
  }
  return obj || {};
};

/*
==========================================
         AG-Grid Functions
==========================================
*/
export const saveGridAPi = api => {
  gridApi = api;
};

export const getGridAPi = () => gridApi;

export const saveGridState = page => {
  // Do the change only when grid api's are avialble
  if (gridApi) {
    const obj = {};
    obj.colState = gridApi.columnApi.getColumnState();
    obj.groupState = gridApi.columnApi.getColumnGroupState();
    obj.sortState = gridApi.api.getSortModel();
    obj.filterState = gridApi.api.getFilterModel();
    writeDataToLocalStorage(page, JSON.stringify(obj));
  }
};

export const restoreGridState = page => {
  // Do the change only when grid api's are avialble
  if (gridApi) {
    const obj = JSON.parse(readDataFromLocalStorage(page));
    gridApi.columnApi.setColumnState(obj.colState);
    gridApi.columnApi.setColumnGroupState(obj.groupState);
    gridApi.api.setSortModel(obj.sortState);
    gridApi.api.setFilterModel(obj.filterState);
  }
};

// Copy to Clipboard
export const copyToClipBoard = data => {
  try {
    const inp = document.createElement("input");
    document.body.appendChild(inp);
    inp.value = data;
    inp.select();
    document.execCommand("copy");
    document.body.removeChild(inp);
    return true;
  } catch (err) {
    return false;
  }
};

// Copy to Clipboard for CSV error file
export const copyToClipBoardCSV = data => {
  try {
    const inp = document.createElement("textarea");
    document.body.appendChild(inp);
    inp.value = data;
    inp.select();
    document.execCommand("copy");
    document.body.removeChild(inp);
    return true;
  } catch (err) {
    return false;
  }
};

export const checkUserPermission = permissionName => {
  const userPermissions = readDataFromLocalStorage("revvsalesUserPermissions");
  return (
    userPermissions &&
    userPermissions.find(permission => permission.name === permissionName)
  );
};

// Empty Object Check
export const isEmptyObj = obj =>
  Object.keys(obj).length === 0 && obj.constructor === Object;

export const priceConverter = price => {
  if (price) {
    const value = Math.abs(Number(price));
    // Nine Zeroes for Billions
    if (value >= 1.0e9) {
      return `${(value / 1.0e9).toFixed(2)}B`;
    }
    // Six Zeroes for Millions
    if (value >= 1.0e6) {
      return `${(value / 1.0e6).toFixed(2)}M`;
    }
    // Three Zeroes for Thousands
    if (value >= 1.0e3) {
      return `${(value / 1.0e3).toFixed(2)}K`;
    }
    // else return the same value
    return value;
  }
  return price;
};

/*
==========================================
         Org Based Flag Function
==========================================
*/

export const checkAppFlag = flagName => {
  const appFlag = readDataFromLocalStorage("appFlag") || {};
  const flag = (appFlag && appFlag[flagName]) || "";
  return flag === "off";
};

export const getAppFlagValue = flagName => {
  const appFlag = readDataFromLocalStorage("appFlag") || {};
  const flag = (appFlag && appFlag[flagName]) || "";
  return flag;
};

// Validate email
export const validateEmail = email => {
  // eslint-disable-next-line no-useless-escape
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+[a-zA-Z\-0-9]+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase().trim());
};

// check the if billing plan if free
export const isFreeTrailBillingPlan = () => {
  const CURRENT_USER = readDataFromLocalStorage("revvsalesUser");
  if (CURRENT_USER) {
    if (
      CURRENT_USER.billing_plan === FREE_TRAIL ||
      CURRENT_USER.billing_plan === ""
    ) {
      return true;
    }
    return false;
  }
  return false;
};

// check the billing plan to see if user is under trial or trial has expired
export const checkIfCurrentPlanAllowsView = view => {
  const USER_INFO = readDataFromLocalStorage("userSignUpInfo");
  if (USER_INFO) {
    if (
      view.hiddenUnderPlans &&
      view.hiddenUnderPlans.includes(USER_INFO.billing_plan)
    ) {
      return false;
    }
    return true;
  }
  return true;
};

export const checkIfCurrentPlanAllowsRender = settingsView => {
  const currentView = SETTINGS_VIEWS.find(
    view => view.settingsView === settingsView,
  );
  return checkIfCurrentPlanAllowsView(currentView);
};

export const isBulkFeatureEnabled = () => {
  const features = readDataFromLocalStorage("revvsalesUserFeatures");
  const enabled =
    (features &&
      Object.prototype.hasOwnProperty.call(
        features,
        FEATURES_NAME.DOCUMENTS_BULK_SEND,
      )) ||
    false;
  return enabled;
};

/*
  check if two is equal
  */
export const equalArray = (array1, array2) => {
  if (!Array.isArray(array1) || !Array.isArray(array2)) return false;

  if (array1 === array2) return true;

  if (array1.length !== array2.length) return false;

  for (let i = 0; i < array1.length; i += 1) {
    if (Array.isArray(array1[i]) && Array.isArray(array2[i])) {
      const equals = equalArray(array1[i], array2[i]);
      if (!equals) return false;
    } else if (typeof array1[i] !== typeof array2[i]) {
      return false;
    } else if (array1[i] instanceof Object) {
      if (!equalArray(Object.keys(array1[i]), Object.keys(array2[i])))
        return false;
    } else if (array1[i] !== array2[i]) {
      return false;
    }
  }
  return true;
};

export const mapPageNameToEvent = page => {
  switch (page) {
    case PATHS.PAGES.PIPELINE.name:
      return EVENTS.EVENT_NAMES.PIPELINE_PAGE_VIEWED;
    case PATHS.PAGES.TEMPLATES.name:
      return EVENTS.EVENT_NAMES.TEMPLATES_PAGE_VIEWED;
    case PATHS.PAGES.DASHBOARD.name:
      return EVENTS.EVENT_NAMES.DASHBOARD_PAGE_VIEWED;
    case PATHS.PAGES.PRODUCTS.name:
      return EVENTS.EVENT_NAMES.PRODUCT_PAGE_VIEWED;
    case PATHS.PAGES.PRICEBOOKS.name:
      return EVENTS.EVENT_NAMES.PRICEBOOK_PAGE_VIEWED;
    case PATHS.PAGES.ASSETS.name:
      return EVENTS.EVENT_NAMES.ASSETS_PAGE_VIEWED;
    case PATHS.PAGES.WORKFLOWS.name:
      return EVENTS.EVENT_NAMES.WORKFLOW_PAGE_VIEWED;
    case PATHS.PAGES.INBOX.name:
      return EVENTS.EVENT_NAMES.INBOX_PAGE_VIEWED;
    default:
      return "";
  }
};

export const pageLoadSetup = page => {
  // set page title
  document.title = page.title;
  // call amplitude
  // amplitude.getInstance().logEvent(mapPageNameToEvent(page.name));
};

export const getAssetFolderUrl = (folderName, folderId) => {
  if (folderId === null) {
    return `/${ASSET_PAGE_URL}`;
  }
  return `/${ASSET_PAGE_URL}/${folderName
    .toLocaleLowerCase()
    .split(" ")
    .join("-")}/${folderId}`;
};

export const truncateString = (name, size, flag, dot) => {
  if (name) {
    let nosDot = "...";
    if (dot) {
      nosDot = "";
      for (let i = 0; i < dot; i += 1) {
        nosDot += ".";
      }
    }
    if (name.length > size) {
      let value = name.slice(0, size);
      if (flag) {
        if (name.length >= size) {
          value += nosDot;
        }
      }
      return value;
    }
    return name;
  }
  return null;
};

export const revvDebounce = (func, threshold, execAsap) => {
  let timeoutId; // handle to setTimeout async task (detection period)

  // eslint-disable-next-line func-names
  return function debounced() {
    const obj = this;

    // eslint-disable-next-line prefer-rest-params
    const args = arguments; // arguments at execution time
    // this is the function which will be executed if/when the threshold expires
    const functionCall = () => {
      // if we're executing at the end of the detection period
      if (!execAsap) {
        func.apply(obj, args); // execute now
      }
      timeoutId = null; // clear timeout handle
    };
    // stop any current detection period
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    // otherwise, if we're not already waiting and we're executing at the beginning of the detection period
    else if (execAsap) {
      func.apply(obj, args);
    }

    timeoutId = setTimeout(functionCall, threshold || 100);
  };
};

// Throttle function: Input as function which needs to be throttled and
//  delay is the time interval in milliseconds
export const revvThrottle = (fun, limit) => {
  let timeoutId = false; // Initially, we're not waiting
  return function () {
    // We return a throttled function
    if (!timeoutId) {
      const context = this;
      // eslint-disable-next-line prefer-rest-params
      const args = arguments; // arguments at execution time
      // If we're not waiting
      fun.apply(context, args); // Execute users function
      timeoutId = true; // Prevent future invocations
      setTimeout(() => {
        // After a period of time
        timeoutId = false; // And allow future invocations
      }, limit);
    }
  };
};

// Detect if the user is using safari browrser
export const isSafariBrowser = () =>
  navigator.userAgent.indexOf("Safari") !== -1 &&
  navigator.userAgent.indexOf("Chrome") === -1;

// Detect if the user is using chrome
export const isChromeBrowser = () => {
  const isChrome = navigator.userAgent.indexOf("Chrome") >= 0;
  const isEdge = navigator.userAgent.indexOf("Edg") >= 0;
  const isSafari = isSafariBrowser();
  const isFirefox = navigator.userAgent.indexOf("Firefox") >= 0;

  return isChrome && !isEdge && !isSafari && !isFirefox;
};

export const isMobileBrowser = () =>
  navigator.userAgent.match(/Android/i) ||
  navigator.userAgent.match(/webOS/i) ||
  navigator.userAgent.match(/iPhone/i) ||
  navigator.userAgent.match(/iPad/i) ||
  navigator.userAgent.match(/iPod/i) ||
  navigator.userAgent.match(/BlackBerry/i) ||
  navigator.userAgent.match(/Windows Phone/i);

export const isMobileAndroidBrowser = () =>
  navigator.userAgent.match(/Android/i) || isChromeBrowser();

export const getDocInternalStage = value => {
  const docStatus = DOCUMENT_INTERNAL_STAGE[value];

  // remove esign expired event for non revv sign
  if (
    docStatus === DOCUMENT_INTERNAL_STAGE.ESIGNATURE_LINK_EXPIRED ||
    docStatus === DOCUMENT_INTERNAL_STAGE.ESIGNATURE_LINK_UNEXPIRED
  ) {
    const appFlag = readDataFromLocalStorage("appFlag");
    if (appFlag && appFlag.esign_type && appFlag.esign_type !== "REVV") {
      return null;
    }
  }

  return docStatus;
};

export const isEnterprisePlan = () => {
  const USER_SIGNUP_INFO = readDataFromLocalStorage("userSignUpInfo");
  if (USER_SIGNUP_INFO) {
    if (ENTERPRISE_PLANS.includes(USER_SIGNUP_INFO.billing_plan)) {
      return true;
    }
  }
  return false;
};

export const isFreeBillingPlan = () => {
  const FREE_PLANS = [
    "essentials",
    "basic",
    "v3.1-basic",
    "v3.2-basic",
    "v3.3-basic",
    "plan-a-free",
    "free-10-21",
    "premium-trial-10-21",
  ];
  const USER_SIGNUP_INFO = readDataFromLocalStorage("userSignUpInfo");
  const PLAN_STATUS = readDataFromLocalStorage("revv_planStatus");
  if (USER_SIGNUP_INFO) {
    if (
      FREE_PLANS.includes(USER_SIGNUP_INFO.billing_plan) ||
      PLAN_STATUS === "in_trial"
    ) {
      return true;
    }
    return false;
  }
  return true;
};

export const customFieldValueTypeCheck = (
  fieldType,
  fieldValue,
  customFieldName,
) => {
  let updatedValue = null;
  switch (fieldType) {
    case "periodValue":
      updatedValue = parseInt(fieldValue, 10);
      break;
    case "rateValue":
      updatedValue = parseInt(fieldValue, 10);
      break;
    case "default": {
      if (customFieldName === "NUMBER") {
        updatedValue = parseInt(fieldValue, 10);
      }
      break;
    }
    default:
      updatedValue = null;
      break;
  }
  return updatedValue;
};

export const triggerStripeJSCall = () =>
  new Promise((resolve, reject) => {
    try {
      const existingScript = document.getElementById("revv-stripe");

      if (!existingScript) {
        const script = document.createElement("script");
        script.src = "https://js.stripe.com/v3/";
        script.id = "revv-stripe";
        document.head.appendChild(script);

        script.onload = () => {
          resolve();
        };
      }

      if (existingScript) resolve();
    } catch (err) {
      reject();
      console.error("asdfasdfasdfadsf");
    }
  });

export const isFormFieldEmpty = (value, type, meta) => {
  let isFilled = false;
  if (type === "CLAUSE" && meta && value) {
    const { subtype } = meta;
    if (subtype === "free_text") {
      if (typeof value === "object") {
        if (value.value) {
          isFilled = true;
        }
      } else {
        isFilled = true;
      }
    } else if (typeof value === "string") {
      isFilled = true;
    } else if (Array.isArray(value)) {
      if (value.length > 0) {
        isFilled = true;
      }
    } else if (typeof value === "object") {
      isFilled = true;
    }
  } else {
    if (value && typeof value !== "string" && typeof value !== "number") {
      const vals = Object.values(value);
      isFilled = !!vals.length;
      if (isFilled) {
        isFilled = !(vals.includes("") || vals.includes(null));
        if (type === "ADDRESS") {
          let count = 0;
          for (let i = 0; i < vals.length; i += 1) {
            if (vals[i]) {
              count += 1;
            }
          }
          if (
            (!vals.addressLineTwo || !vals.addressLineTwo) &&
            (count === 4 || count === 5)
          ) {
            isFilled = true;
          }
        } else if (type === "PERSON_NAME") {
          isFilled = !!vals[0] || !!vals[1];
        }
      }
    }
    if (typeof value === "string" || typeof value === "number") {
      if (!(value === null || value === undefined || value === "")) {
        isFilled = true;
      }
    }
  }
  return !isFilled;
};

export const setProductPricebookCatalogClause = (
  fieldUsed,
  productIds,
  productCatalogs,
  // docComponents,
) => {
  let catalogValue = [];
  let catalogFinalvalue = [];
  let catalogPricebook = [];
  let catalogFinalPricebookvalue = [];
  const payload = [];
  const newData = [];
  productIds.forEach(data => {
    productCatalogs.forEach(item => {
      if (item.id === data) {
        newData.push(item);
      }
    });
  });
  fieldUsed.forEach(item => {
    if (
      item.meta.subtype === "product_catalog_column" ||
      item.meta.subtype === "price_catalog_column"
    ) {
      // item.meta.catalog_column;
      newData.forEach(data => {
        const product = productIds.includes(data.id);
        if (product) {
          if (item.meta.subtype === "product_catalog_column") {
            catalogValue.push(data.product);
          } else if (item.meta.subtype === "price_catalog_column") {
            catalogPricebook.push(data);
          }
        }
      });
      catalogValue.forEach(value => {
        let productValue;
        if (item.meta.catalog_column === "desc") {
          productValue = value.description;
        } else if (item.meta.catalog_column === "ptype") {
          productValue = value.type;
        } else {
          productValue =
            value[item.meta.catalog_column] ||
            (value.custom_fields !== null &&
              value.custom_fields[item.meta.catalog_column]);
        }
        if (productValue) {
          catalogFinalvalue.push(productValue);
        }
      });

      catalogPricebook.forEach(pr => {
        let priceValue = "";
        let priceData;
        if (item.meta.catalog_column.includes("product")) {
          priceValue = item.meta.catalog_column.substring(
            8,
            item.meta.catalog_column.length,
          );
        }

        if (priceValue !== "") {
          if (priceValue === "desc") {
            priceData = pr.product.description;
          } else if (pr.product[priceValue]) {
            priceData = pr.product[priceValue];
          } else if (
            pr.product.custom_fields !== null &&
            pr.product.custom_fields[priceValue]
          ) {
            priceData = pr.product.custom_fields[priceValue];
          }
        } else if (pr[item.meta.catalog_column]) {
          priceData = pr[item.meta.catalog_column];
        } else if (
          pr.custom_fields !== null &&
          pr.custom_fields[item.meta.catalog_column]
        ) {
          priceData = pr.custom_fields[item.meta.catalog_column];
        }
        if (
          typeof priceData === "object" &&
          Object.keys(priceData).length > 0
        ) {
          priceData = priceData.type;
        }
        if (priceData) {
          catalogFinalPricebookvalue.push(priceData);
        }
      });
      let catalogData = "";
      let displayType;
      let catalogPriceData = "";
      // catalogFinalvalue.reverse();
      // catalogFinalPricebookvalue.reverse();
      if (item.meta.catalog_display === "commaSeperated") {
        catalogData = catalogFinalvalue.toString();
        catalogPriceData = catalogFinalPricebookvalue.toString();
      } else if (item.meta.catalog_display === "numberedPoints") {
        displayType = "ordered-list-1";
        catalogData = catalogFinalvalue.join("\n");
        catalogPriceData = catalogFinalPricebookvalue.join("\n");
      } else if (item.meta.catalog_display === "bulletPoints") {
        displayType = "unordered-list-1";
        catalogData = catalogFinalvalue.join("\n");
        catalogPriceData = catalogFinalPricebookvalue.join("\n");
      } else if (item.meta.catalog_display === "paragraph") {
        catalogData = catalogFinalvalue.join("\n");
        catalogPriceData = catalogFinalPricebookvalue.join("\n");
      }
      let editorState;
      if (
        item.meta.subtype === "product_catalog_column" &&
        catalogData !== ""
      ) {
        editorState = EditorState.createWithContent(
          ContentState.createFromText(catalogData),
        );
      } else if (
        item.meta.subtype === "price_catalog_column" &&
        catalogPriceData !== ""
      ) {
        editorState = EditorState.createWithContent(
          ContentState.createFromText(catalogPriceData),
        );
      }
      if (displayType && editorState) {
        const rawContentState = convertToRaw(editorState.getCurrentContent());
        const { blocks } = rawContentState;
        blocks.forEach(stateValue => {
          stateValue.type = displayType;
        });
        const updatedContentState = convertFromRaw(rawContentState);
        editorState = EditorState.createWithContent(updatedContentState);
      }
      let stateValue;
      if (editorState) {
        stateValue = JSON.stringify(
          convertToRaw(editorState.getCurrentContent()),
        );
      }
      if (
        catalogFinalvalue.length > 0 ||
        catalogFinalPricebookvalue.length > 0
      ) {
        const data = {
          filler_type: "SENDER",
          id: item.id,
          value: { value: stateValue },
        };
        payload.push(data);
      } else {
        const data = {
          filler_type: "SENDER",
          id: item.id,
          description: item.field_name,
          field_name: item.field_name,
        };
        payload.push(data);
      }

      catalogValue = [];
      catalogFinalvalue = [];
      catalogFinalPricebookvalue = [];
      catalogPricebook = [];
    } else if (
      item.meta.subtype === "product_catalog_column" ||
      item.meta.subtype === "price_catalog_column"
    ) {
      const data = {
        id: item.id,
        value: { value: "" },
      };
      payload.push(data);
      // } else if (item.type === "TABLE") {
      //   // eslint-disable-next-line no-console
      //   console.log(
      //     "docComponents[item.occurrences[0].componentId].content.data;",
      //     docComponents[item.occurrences[0].componentId].content.data,
      //   );
      //   const getValue =
      //     docComponents[item.occurrences[0].componentId].content.data;
      //   const data = {
      //     id: item.id,
      //     filler_type: item.filler_type,
      //     description: item.field_name,
      //     field_name: item.field_name,
      //     value: getValue,
      //   };
      //   payload.push(data);
    } else {
      let fieldValue = item.value;
      if (item.value && item.value.currency) {
        fieldValue = item.value.val;
      }
      if (
        item.type === "CLAUSE" &&
        item.meta.subtype === "free_text" &&
        typeof item.value === "string"
      ) {
        fieldValue = {
          value: item.value,
        };
      }
      const data = {
        id: item.id,
        filler_type: item.filler_type,
        description: item.field_name,
        field_name: item.field_name,
        value: fieldValue,
      };
      payload.push(data);
    }
  });
  return payload;
};

export const hexToRgbA = hex => {
  let c;
  if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    c = hex.substring(1).split("");
    if (c.length === 3) {
      c = [c[0], c[0], c[1], c[1], c[2], c[2]];
    }
    c = `0x${c.join("")}`;
    // eslint-disable-next-line no-bitwise
    return `rgba(${[(c >> 16) & 255, (c >> 8) & 255, c & 255].join(",")},1)`;
  }
  return null;
};

// export const checkFreePlan = plan_type => {
//   if (plan_type === "FREE") return true;
//   return false;
// };

export const checkFreePlan = () => {
  const planType = readDataFromLocalStorage("revvsalesUserPlanType");
  if (planType === "FREE") return true;
  return false;
};

export function dateDiff(date1, date2, type = "DAYS") {
  if (date1 && date2) {
    let MS_PER = 1000 * 60 * 60 * 24;
    if (type === "DAYS") {
      MS_PER = 1000 * 60 * 60 * 24;
    } else if (type === "HRS") {
      MS_PER = 1000 * 60 * 60;
    } else if (type === "MIN") {
      MS_PER = 1000 * 60;
    } else if (type === "SEC") {
      MS_PER = 1000;
    }

    if (date2.getTime() - date1.getTime() < 0) {
      return null;
    }

    return Math.ceil(Math.abs(date2.getTime() - date1.getTime()) / MS_PER);
  }
  return null;
}
